// src/components/QuestionRow.jsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  Trash2,
  GripVertical,
  Copy,
  Pencil,
  PenLine,
  ChevronDown,
  CheckSquare,
  Calendar,
  Image as ImageIcon,
  SlidersHorizontal,
  Edit3,
  Info,
} from "lucide-react";
import { useBuilderStore } from "../store/builderStore";
import { API_BASE } from "../lib/api";
import SliderSettingsPanel from "./SliderSettingsPanel";
import MediaSettingsPanel from "./MediaSettingsPanel";

function MCResponsePanel({ sectionId, question, onUseSet }) {
  const {
    templateSets,
    globalSets,
    sections,
    addTemplateSet,
    addGlobalSet,
    updateTemplateSet,
    updateGlobalSet,
  } = useBuilderStore();

  const [query, setQuery] = useState("");

  // frequency of use within THIS template
  const usageMap = useMemo(() => {
    const freq = new Map();
    (sections || []).forEach((s) =>
      (s.questions || []).forEach((q) => {
        const r = q?.responseSet;
        if (r?.id && (r.type === "template" || r.type === "global")) {
          const key = `${r.type}:${r.id}`;
          freq.set(key, (freq.get(key) || 0) + 1);
        }
      })
    );
    return freq;
  }, [sections]);

  const filterByQuery = (list) => {
    if (!query.trim()) return list;
    const q = query.toLowerCase();
    return list.filter(
      (s) =>
        (s.name || "").toLowerCase().includes(q) ||
        (s.options || []).some((o) => (o || "").toLowerCase().includes(q))
    );
  };

  const sortByUsage = (items, type) => {
    return [...items].sort((a, b) => {
      const ka = `${type}:${a.id}`;
      const kb = `${type}:${b.id}`;
      const ua = usageMap.get(ka) || 0;
      const ub = usageMap.get(kb) || 0;
      if (ub !== ua) return ub - ua; // most used first
      return (a.name || "").localeCompare(b.name || "");
    });
  };

  const templateSorted = sortByUsage(filterByQuery(templateSets || []), "template");
  const globalSorted = sortByUsage(filterByQuery(globalSets || []), "global");

  const editSet = (type, setObj) => {
    const newName = prompt("Set name:", setObj.name) ?? setObj.name;
    const csv = prompt("Comma-separated options:", (setObj.options || []).join(", "));
    const nextOpts =
      typeof csv === "string"
        ? csv
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : setObj.options || [];

    if (type === "template") updateTemplateSet(setObj.id, { name: newName, options: nextOpts });
    else updateGlobalSet(setObj.id, { name: newName, options: nextOpts });
  };

  return (
    <div className="mc-col">
      {/* Search */}
      <div className="mc-search">
        <input
          className="question-input mc-search-input"
          placeholder="Search response sets..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {/* Template (Multiple choice responses) */}
      <div className="mc-section">
        <div className="mc-section-header">
          <span>Multiple choice responses</span>
          <button
            type="button"
            className="btn-link"
            onClick={() => addTemplateSet("New Template Set")}
          >
            + Responses
          </button>
        </div>

        {templateSorted.map((s) => (
          <div
            key={s.id}
            className="mc-row"
            onClick={() => onUseSet("template", s.id)}
          >
            <div className="mc-row-left">
              <div className="mc-row-title">{s.name}</div>
              <div className="mc-chipline">
                {(s.options || []).slice(0, 6).map((o, i) => (
                  <span key={i} className="chip">{o}</span>
                ))}
                {(s.options || []).length > 6 && (
                  <span className="chip">+{(s.options || []).length - 6}</span>
                )}
              </div>
            </div>
            <div className="mc-row-actions">
              <button
                type="button"
                className="icon-btn"
                title="Edit"
                onClick={(e) => {
                  e.stopPropagation();
                  editSet("template", s);
                }}
              >
                <Pencil size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Global Response Sets */}
      <div className="mc-section">
        <div className="mc-section-header">
          <span>Global Response Sets</span>
          <button
            type="button"
            className="btn-link"
            onClick={() => addGlobalSet("New Global Set")}
          >
            + Create
          </button>
        </div>

        {globalSorted.map((s) => (
          <div
            key={s.id}
            className="mc-row"
            onClick={() => onUseSet("global", s.id)}
          >
            <div className="mc-row-left">
              <div className="mc-row-title">{s.name}</div>
              <div className="mc-chipline">
                {(s.options || []).slice(0, 6).map((o, i) => (
                  <span key={i} className="chip">{o}</span>
                ))}
                {(s.options || []).length > 6 && (
                  <span className="chip">+{(s.options || []).length - 6}</span>
                )}
              </div>
            </div>
            <div className="mc-row-actions">
              <button
                type="button"
                className="icon-btn"
                title="Edit"
                onClick={(e) => {
                  e.stopPropagation();
                  editSet("global", s);
                }}
              >
                <Pencil size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// Options are rendered in two visual groups directly in the <select>
// to support unique types and headers like "Title page information" / "Other responses".

export default function QuestionRow({
  sectionId,
  question,
  dragHandleProps,
  indentLevel = 0,
  isSectionCollapsed = false,
  onToggleSectionCollapse,
  rowRef = null,
}) {
  const {
    updateQuestion,
    deleteQuestion,
    duplicateQuestion,
    activeQuestion,
    setActiveQuestion,
    setActiveRow,
  } = useBuilderStore();

  // ---- Dropdown menu state for type selector ----
  const [typeOpen, setTypeOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const menuRef = useRef(null);
  const typeButtonRef = useRef(null);

  // Hover state for subtle row highlight
  const [isHovered, setIsHovered] = useState(false);

  // Slider settings side panel
  const [sliderPanelOpen, setSliderPanelOpen] = useState(false);
  const [mediaPanelOpen, setMediaPanelOpen] = useState(false);
  const [uploadingInstructionFile, setUploadingInstructionFile] = useState(false);

  // Ensure only ONE type menu is open globally across all QuestionRow instances
  useEffect(() => {
    const handleGlobalClose = () => {
      setTypeOpen(false);
      setMenuPosition(null);
    };

    window.addEventListener("moravia-type-menu-close", handleGlobalClose);
    return () => {
      window.removeEventListener("moravia-type-menu-close", handleGlobalClose);
    };
  }, []);

  // Close this menu when clicking anywhere outside the menu AND the type button
  useEffect(() => {
    if (!typeOpen) return;

    const handleClickOutside = (e) => {
      const menuEl = menuRef.current;
      const btnEl = typeButtonRef.current;
      if (!menuEl) return;

      const clickInsideMenu = menuEl.contains(e.target);
      const clickOnButton = btnEl && btnEl.contains(e.target);

      if (!clickInsideMenu && !clickOnButton) {
        setTypeOpen(false);
        setMenuPosition(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [typeOpen]);

  // Keep the menu attached to the button on scroll/resize
  useEffect(() => {
    if (!typeOpen) return;

    const updatePosition = () => {
      if (!typeButtonRef.current) return;
      const rect = typeButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    };

    // Capture scroll events from ANY scrollable ancestor
    document.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    // Initial sync
    updatePosition();

    return () => {
      document.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [typeOpen]);

  // unique types available only once per template
  const UNIQUE_TYPES = ["site", "document-number", "location", "person"];

  // compute which unique types are already used across the template
  const allSections = useBuilderStore((s) => s.sections);
  const usedUnique = new Set();
  allSections.forEach((sec) => {
    (sec.questions || []).forEach((q) => {
      if (UNIQUE_TYPES.includes(q.type)) usedUnique.add(q.type);
    });
  });
  const isUsedElsewhere = (t) => usedUnique.has(t) && question.type !== t;

  const isActive =
    activeQuestion &&
    activeQuestion.sectionId === sectionId &&
    activeQuestion.questionId === question.id;

  const isMobile = window.innerWidth < 768;

  // map type -> icon (for the "Type of response" cell)
  const typeIcon = {
    signature: <PenLine size={16} />,
    checkbox: <CheckSquare size={16} />,
    date: <Calendar size={16} />,
    media: <ImageIcon size={16} />,
    slider: <SlidersHorizontal size={16} />,
    annotation: <Edit3 size={16} />,
    instruction: <Info size={16} />,
  };

  // safe getters for new flags
  const isRequired = !!question.required;
  const isMulti = !!question.multipleSelection;
  const isFlagged = !!question.flaggedResponses;
  const isLinked = !!question.linkResponse;
  const hasLogic = !!question.logicEnabled;
  const isSection = question.type === "section";
  const instructionStyle = question.instructionStyle || "info"; // info | danger | caution | positive

  // Shared background with hover: questions = white -> light gray, sections = blue -> lighter blue
  const baseQuestionBg = "#ffffff";
  const baseSectionBg = "#e0ecff";
  const hoverQuestionBg = "#f9fafb";
  const hoverSectionBg = "#dbeafe";
  const rowBg = isSection
    ? (isHovered ? hoverSectionBg : baseSectionBg)
    : (isHovered ? hoverQuestionBg : baseQuestionBg);

  const activeRowBg = isSection ? "#dbeafe" : "#eff6ff";

  // Card visual styling: the row itself is the only visible element.
  // No vertical padding so there is ZERO gap between stacked rows.
  const cardStyle = isSection
    ? {
        background: isActive ? activeRowBg : rowBg,
        borderRadius: 0,
        borderBottom: "0px solid #d0e2ff",
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 12,
        paddingRight: 12,
        marginTop: 0,
        marginBottom: 0,
        borderLeft: isActive ? "3px solid #2563eb" : "3px solid transparent", // SC-style active border
      }
    : {
        background: isActive ? activeRowBg : rowBg,
        borderRadius: 0,
        borderBottom: "1px solid #e5e7eb",
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 12,
        paddingRight: 12,
        marginTop: 0,
        marginBottom: 0,
        borderLeft: isActive ? "3px solid #2563eb" : "3px solid transparent", // SC-style active border
      };

  // Outer wrapper is just for indent/nesting – no visible chrome, no extra height
  const rowStyle = {
    marginLeft: 0, // indentation now controlled by TemplateBuilder
    background: "transparent",
    border: "none",
    boxShadow: "none",
    marginTop: 0,
    marginBottom: 0,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 8, // slight gap from indent line → SC-like spacing
  };

  // slider config (per-question)
  const sliderMin = typeof question.sliderMin === "number" ? question.sliderMin : 0;
  const sliderMax = typeof question.sliderMax === "number" ? question.sliderMax : 10;
  const sliderStep =
    typeof question.sliderStep === "number" && question.sliderStep > 0
      ? question.sliderStep
      : 1;

  const handleInstructionFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    try {
      setUploadingInstructionFile(true);
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(`${API_BASE}/upload`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        throw new Error("Upload failed");
      }

      const data = await res.json();
      const url = data.url || data.path;
      if (!url) {
        throw new Error("Upload failed: no URL returned");
      }

      updateQuestion(sectionId, question.id, {
        instructionMediaUrl: url,
        instructionMediaName: file.name,
      });
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to upload file");
    } finally {
      setUploadingInstructionFile(false);
      // allow re-selecting same file
      if (e.target) e.target.value = "";
    }
  };

  return (
    <div
      className="indent-wrapper"
      style={{ paddingLeft: indentLevel * 16 }}
      ref={rowRef}
      data-row-id={question?.id}
    >
      {indentLevel > 0 && !isSection && (
        <div
          className="indent-line"
          style={{
            left: (indentLevel - 1) * 16 - 8,
          }}
        />
      )}
      <div
        className={`question-row ${isActive ? "is-active" : ""}`}
        onClick={() => {
          if (setActiveRow) {
            setActiveRow(question.id);
          }
          setActiveQuestion(sectionId, question.id);
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={rowStyle}
      >
      {/* --- SAFETYCULTURE-STYLE ROW GRID (row itself is the card) --- */}
      <div
        className="qr-card"
        style={cardStyle}
      >
        {/* top row: two columns */}
        <div
          className="qr-grid"
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "minmax(0, 1fr)"
              : "minmax(0, 1.6fr) minmax(0, 1fr)",
            columnGap: 16,
            rowGap: isMobile ? 8 : 0,
            alignItems: "center",
          }}
        >
          {/* LEFT: Question / Section text + drag handle */}
          <div className="qr-col-left">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {isSection && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleSectionCollapse && onToggleSectionCollapse();
                  }}
                  style={{
                    border: "none",
                    background: "transparent",
                    padding: 6,
                    marginRight: 4,
                    width: 32,
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                  aria-label={isSectionCollapsed ? "Expand section" : "Collapse section"}
                >
                  <ChevronDown
                    size={16}
                    style={{
                      transform: isSectionCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
                      transition: "transform 0.15s ease",
                      color: "#4b5563",
                    }}
                  />
                </button>
              )}
              <span
                className="drag-handle"
                {...dragHandleProps}
                onClick={(e) => e.stopPropagation()}
                style={{
                  padding: 6,
                  width: 28,
                  height: 28,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <GripVertical size={18} />
              </span>
              <input
                className="qr-input"
                style={{
                  backgroundColor: rowBg,
                  border: "none",
                  boxShadow: "none",
                  borderRadius: 0,
                  marginTop: 0,
                  marginBottom: 0,
                  paddingTop: 6,
                  paddingBottom: 6,
                }}
                value={question.label}
                placeholder={isSection ? "New section" : "Type question"}
                onFocus={() => setActiveQuestion(sectionId, question.id)}
                onChange={(e) =>
                  updateQuestion(sectionId, question.id, { label: e.target.value })
                }
              />
            </div>
          </div>

          {/* RIGHT: Type of response (button + selected icon, or section controls) */}
          <div className="qr-col-right">
            <div className="qr-type-selector">
              {isSection ? (
                // SECTION ROW: show inline "Repeat this section" instead of a type dropdown
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <label className="qr-toggle">
                    <input
                      type="checkbox"
                      checked={!!question.repeatSection}
                      onChange={() =>
                        updateQuestion(sectionId, question.id, {
                          repeatSection: !question.repeatSection,
                        })
                      }
                    />
                    <span>Repeat this section</span>
                  </label>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    className="type-menu-button qr-type-button"
                    ref={typeButtonRef}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveQuestion(sectionId, question.id);

                      // If we're about to open this menu, close all others first
                      if (!typeOpen) {
                        window.dispatchEvent(new Event("moravia-type-menu-close"));
                      }

                      if (!typeOpen && typeButtonRef.current) {
                        const rect = typeButtonRef.current.getBoundingClientRect();
                        setMenuPosition({
                          top: rect.bottom + window.scrollY,
                          left: rect.left + window.scrollX,
                          width: rect.width,
                        });
                      } else if (typeOpen) {
                        // Closing this menu
                        setMenuPosition(null);
                      }

                      setTypeOpen((v) => !v);
                    }}
                    style={{
                      backgroundColor: rowBg,
                      borderRadius: 0,
                      marginTop: 0,
                      marginBottom: 0,
                      paddingTop: 6,
                      paddingBottom: 6,
                    }}
                  >
                    <span className="qr-type-icon">
                      {typeIcon[question.type] || null}
                    </span>
                    <span className="qr-type-text">
                      {(() => {
                        const labels = {
                          site: "Site",
                          "document-number": "Document number",
                          location: "Location",
                          person: "Person",
                          multiple_choice: "Multiple Choice",
                          checkbox: "Checkbox",
                          date: "Date & Time",
                          media: "Media Upload",
                          slider: "Slider",
                          signature: "Signature",
                          annotation: "Annotation",
                          instruction: "Instruction",
                        };
                        return labels[question.type] || "Select type";
                      })()}
                    </span>
                    <span className="type-menu-caret">▾</span>
                  </button>

                  {/* dropdown menu rendered in a portal so it isn't clipped by row height */}
                  {typeOpen &&
                    menuPosition &&
                    createPortal(
                      <div
                        className="type-menu"
                        ref={menuRef}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: "absolute",
                          top: menuPosition.top,
                          // Shift left so the types column starts under the dropdown button
                          left: menuPosition.left - 360,
                          zIndex: 9999,
                          maxHeight: "calc(100vh - 40px)",
                          overflowY: "auto",
                        }}
                      >
                        {/* LEFT column: response sets (ALWAYS visible) */}
                        <div className="type-menu-left">
                          <MCResponsePanel
                            sectionId={sectionId}
                            question={question}
                            onUseSet={(type, id) => {
                              updateQuestion(sectionId, question.id, {
                                type: "multiple_choice",
                                responseSet: { type, id },
                                options: null,
                              });
                              setTypeOpen(false);
                              setMenuPosition(null);
                            }}
                          />
                        </div>

                        {/* RIGHT column: answer types (NO 'Multiple Choice' here) */}
                        <div className="type-menu-right">
                          <div
                            className="type-menu-group-header"
                            style={{ padding: "6px 8px" }}
                          >
                            Title page information
                          </div>
                          {[
                            { v: "site", label: "Site" },
                            { v: "document-number", label: "Document number" },
                            { v: "location", label: "Location" },
                            { v: "person", label: "Person" },
                          ].map((t) => (
                            <button
                              key={t.v}
                              type="button"
                              className={`type-item ${
                                question.type === t.v ? "active" : ""
                              }`}
                              disabled={isUsedElsewhere(t.v)}
                              title={
                                isUsedElsewhere(t.v)
                                  ? "Can be used only once per template"
                                  : ""
                              }
                              onClick={() => {
                                updateQuestion(sectionId, question.id, {
                                  type: t.v,
                                  responseSet: null,
                                });
                                setTypeOpen(false);
                                setMenuPosition(null);
                              }}
                            >
                              {t.label}
                            </button>
                          ))}

                          <div
                            className="type-menu-group-header"
                            style={{ padding: "10px 8px 6px" }}
                          >
                            Other responses
                          </div>
                          {[
                            // NOTE: "section" REMOVED here – Section is now structural only
                            { v: "checkbox", label: "Checkbox" },
                            { v: "date", label: "Date & Time" },
                            { v: "media", label: "Media Upload" },
                            { v: "slider", label: "Slider" },
                            { v: "signature", label: "Signature" },
                            { v: "annotation", label: "Annotation" },
                            { v: "instruction", label: "Instruction" },
                          ].map((t) => (
                            <button
                              key={t.v}
                              type="button"
                              className={`type-item ${
                                question.type === t.v ? "active" : ""
                              }`}
                              onClick={() => {
                                const payload = {
                                  ...question,
                                  type: t.v,
                                  responseSet: null,
                                };

                                if (t.v === "slider") {
                                  payload.sliderMin = sliderMin;
                                  payload.sliderMax = sliderMax;
                                  payload.sliderStep = sliderStep;
                                }

                                if (t.v === "media") {
                                  payload.allowMultiple =
                                    typeof question.allowMultiple === "boolean"
                                      ? question.allowMultiple
                                      : true;
                                  payload.allowComments =
                                    typeof question.allowComments === "boolean"
                                      ? question.allowComments
                                      : true;
                                  payload.maxFiles =
                                    typeof question.maxFiles === "number"
                                      ? question.maxFiles
                                      : "";
                                }

                                // Instruction is info-only: clear answer-related flags
                                if (t.v === "instruction") {
                                  payload.required = false;
                                  payload.multipleSelection = false;
                                  payload.flaggedResponses = false;
                                  payload.linkResponse = false;
                                  payload.logicEnabled = false;
                                }

                                updateQuestion(sectionId, question.id, payload);
                                setTypeOpen(false);
                                setMenuPosition(null);

                                if (t.v === "slider") {
                                  setSliderPanelOpen(true);
                                }
                                if (t.v === "media") {
                                  setMediaPanelOpen(true);
                                }
                              }}
                            >
                              {t.label}
                            </button>
                          ))}
                        </div>
                      </div>,
                      document.body
                    )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* bottom options bar (accordion: only for active question) */}
        {isActive && (
          <div className="qr-options">
            {question.type !== "instruction" && question.type !== "section" && (
              <>

                <button
                  type="button"
                  className={`qr-link ${hasLogic ? "on" : ""}`}
                  onClick={() =>
                    updateQuestion(sectionId, question.id, { logicEnabled: !hasLogic })
                  }
                >
                  Add logic
                </button>

                <label className="qr-toggle">
                  <input
                    type="checkbox"
                    checked={isRequired}
                    onChange={() =>
                      updateQuestion(sectionId, question.id, { required: !isRequired })
                    }
                  />
                  <span>Required</span>
                </label>

                {(question.type === "multiple_choice" ||
                  question.type === "checkbox") && (
                  <label className="qr-toggle">
                    <input
                      type="checkbox"
                      checked={isMulti}
                      onChange={() =>
                        updateQuestion(sectionId, question.id, {
                          multipleSelection: !isMulti,
                        })
                      }
                    />
                    <span>Multiple selection</span>
                  </label>
                )}

                <label className={`qr-pill ${isFlagged ? "yes" : "no"}`}>
                  <input
                    type="checkbox"
                    checked={isFlagged}
                    onChange={() =>
                      updateQuestion(sectionId, question.id, {
                        flaggedResponses: !isFlagged,
                      })
                    }
                  />
                  <span>Flagged responses</span>
                  <em className="pill-state">{isFlagged ? "Yes" : "No"}</em>
                </label>

                <label className="qr-toggle">
                  <input
                    type="checkbox"
                    checked={isLinked}
                    onChange={() =>
                      updateQuestion(sectionId, question.id, {
                        linkResponse: !isLinked,
                      })
                    }
                  />
                  <span>Link response</span>
                </label>
              </>
            )}

            {question.type === "instruction" && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 12,
                  width: "100%",
                }}
              >
                {/* File controls on the left */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <label
                    className="qr-link"
                    style={{ cursor: "pointer", margin: 0 }}
                  >
                    {uploadingInstructionFile
                      ? "Uploading..."
                      : question.instructionMediaUrl
                      ? "Change file"
                      : "Attach file"}
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      style={{ display: "none" }}
                      onChange={handleInstructionFileChange}
                    />
                  </label>

                  {question.instructionMediaUrl && (
                    <button
                      type="button"
                      className="qr-link"
                      onClick={() =>
                        updateQuestion(sectionId, question.id, {
                          instructionMediaUrl: null,
                          instructionMediaName: null,
                        })
                      }
                    >
                      Remove
                    </button>
                  )}
                </div>

                {/* Thumbnail preview */}
                {question.instructionMediaUrl && (
                  <div
                    style={{
                      marginTop: 6,
                      marginLeft: 2,
                    }}
                  >
                    {question.instructionMediaUrl.toLowerCase().includes(".pdf") ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "6px 10px",
                          background: "#f3f4f6",
                          border: "1px solid #e5e7eb",
                          borderRadius: 6,
                          fontSize: 13,
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>PDF:</span>
                        <span>{question.instructionMediaName || "Document"}</span>
                      </div>
                    ) : (
                      <img
                        src={question.instructionMediaUrl}
                        alt={question.instructionMediaName || "Preview"}
                        style={{
                          width: 110,
                          height: 70,
                          objectFit: "cover",
                          borderRadius: 6,
                          border: "1px solid #e5e7eb",
                          display: "block",
                        }}
                      />
                    )}
                  </div>
                )}

                {/* Instruction type chips to the right of file controls */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 8,
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 500 }}>
                    Instruction type:
                  </span>
                  {[
                    { key: "info", label: "Regular" },
                    { key: "danger", label: "Danger" },
                    { key: "caution", label: "Caution" },
                    { key: "positive", label: "Positive" },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() =>
                        updateQuestion(sectionId, question.id, {
                          instructionStyle: opt.key,
                        })
                      }
                      style={{
                        fontSize: 12,
                        padding: "4px 10px",
                        borderRadius: 999,
                        border:
                          instructionStyle === opt.key
                            ? "1px solid #2563eb"
                            : "1px solid #e5e7eb",
                        background:
                          instructionStyle === opt.key
                            ? "rgba(37,99,235,0.08)"
                            : "#ffffff",
                        cursor: "pointer",
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {question.type === "slider" && (
              <button
                type="button"
                className="qr-link"
                onClick={() => setSliderPanelOpen(true)}
                style={{ marginLeft: 16 }}
              >
                Range {sliderMin} to {sliderMax} step {sliderStep}
              </button>
            )}

            {/* right-side actions */}
            <div className="qr-actions">
              <button
                className="icon-btn"
                title="Duplicate"
                onClick={(e) => {
                  e.stopPropagation();
                  duplicateQuestion(sectionId, question.id);
                }}
              >
                <Copy size={16} />
              </button>
              <button
                className="icon-btn"
                title="Delete"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteQuestion(sectionId, question.id);
                }}
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      <SliderSettingsPanel
        open={sliderPanelOpen}
        onClose={() => setSliderPanelOpen(false)}
        sectionId={sectionId}
        question={question}
      />

      <MediaSettingsPanel
        open={mediaPanelOpen}
        onClose={() => setMediaPanelOpen(false)}
        sectionId={sectionId}
        question={question}
      />

      {/* moved: MC response set chooser now appears in dropdown, this block removed */}
      </div>
    </div>
  );
}
